import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { SovosPlatformProvider } from "mosaic-react-platform";

import { SovosGlobalLayout, SovosSnackbar } from "mosaic-react";

import { withRouter } from "react-router-dom";
import I18n from "../I18n";
import { stringifyFilter } from "colbi_web_ui/lib/utils";
import SovosProvider from "mosaic-react/sovos-provider/SovosProvider";
import { GlobalProvider as ColbiUIProvider } from "colbi_web_ui/lib/state/GlobalProvider";
import { GlobalProvider } from "../state/globalProvider";
import { PersistGate } from "redux-persist/integration/react";
import { persistor } from "../index";

import { removeModal } from "../store/actions";
import { buildSovosSlotProps } from "../utils/utils";

import { deepLink } from "../utils/routingUtils";

import configsByTheme from "../enums/configsByTheme";
import proxy from "../routes/proxy";
import theme from "../style/_theme.json";
import LanguageDialog from "../components/LanguageDialog/LanguageDialog";
import ModalController from "../controllers/App/ModalController/ModalController";
import DialogController from "../controllers/App/DialogController/DialogController";
import {
    REACT_APP_COLBI_THEME,
    REACT_APP_LEARN_MORE_URL,
    REACT_APP_PRIVACY_POLICY_URL,
    REACT_APP_SSO_BASE_URL,
    REACT_APP_TERMS_AND_CONDITIONS_URL,
    REACT_APP_ENVIRONMENT_TYPE,
} from "../environmentVariables";

import globalStyles from "../style/globalStyles.sass";
import { AboutThisDialog } from "./AboutThisDialog/AboutThisDialog";
import { useAuth } from "react-oidc-context";

import environmentIndex from "./environmentSovosPlatform.json";
import * as detailPanels from "./DetailPanels/index";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as customCells from "./CustomCells/index";

const Provider = ({ children, ...props }) => {
    return (
        <GlobalProvider {...props}>
            <ColbiUIProvider {...props}>{children}</ColbiUIProvider>
        </GlobalProvider>
    );
};

const appTheme = REACT_APP_COLBI_THEME ? REACT_APP_COLBI_THEME.toLocaleLowerCase() : "sovos";
const localesByAppTheme = configsByTheme(appTheme)["locales"];

const GlobalLayout = ({ children, ...props }) => {
    const {
        logout,
        pushModal,
        popModal,
        genericSearchAction,
        setLocale,
        match,
        location,
        history,
        reload,
        uploads,
        pushDialog,
        uploadFiles,
        download,
        submitAuditNote,
        setDataFileStatus,
        deleteFilePermanently,
        deleteInvalidFilePermanently,
        hideFile,
        downloadFile,
        reprocessRepository,
        auditNote,
        getFindingDetails,
        downloadJSON,
        submitTicket,
        reprocessFiles,
        navProps,
        downloadableQueries,
        params,
        repositoryStatus,
        getTotalExportLines,
        styles,
        version,
    } = props;

    const { links: staticLinks, userInfo: userInformation } = navProps;

    const environmentProductSwitcherSetup = environmentIndex[REACT_APP_ENVIRONMENT_TYPE] || 0;

    const onLoadingComplete = () => {
        if (REACT_APP_ENVIRONMENT_TYPE === "rac") {
            const productSwitcherButton = document.querySelector(
                '[data-testid="sovosProductMenu__button"]',
            );
            productSwitcherButton &&
                (productSwitcherButton.onclick = (e) => {
                    e.stopPropagation();
                });
        }
    };

    const finalStyles = {
        ...styles,
        ...globalStyles,
    };

    const locale = match.params.locale || "en";
    const i18n = I18n.use(locale);
    const _selectedLink = location?.state?.selectedLink || i18n("overview");
    const auth = useAuth();

    const [userLocale, setUserLocale] = useState(locale);
    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
    const [isAboutThisDialogOpen, setIsAboutThisDialogOpen] = useState(false);
    const [selectedLink, setSelectedLink] = useState(_selectedLink);
    const [notification, pushNotification] = useState({ open: false, message: "", type: "" });

    useEffect(() => {
        if (location && location.state && location.state.selectedLink) {
            setSelectedLink(location.state.selectedLink);
        }
    }, [location, props]);

    useEffect(() => {
        deepLink(staticLinks, location.pathname, setSelectedLink);
    }, [staticLinks, location, setSelectedLink]);

    const fullNavProps = {
        ...navProps,
        ...buildSovosSlotProps(
            history,
            locale,
            userInformation,
            setIsLanguageModalOpen,
            logout,
            i18n,
            selectedLink,
            setIsAboutThisDialogOpen,
            auth,
        ),
    };

    const goto = (page, filters, anchor) => {
        const pageUrl = page
            ? (typeof page.join === "function" ? page.join("/") : page).replace(/\/+/g, "/")
            : null;
        const urlFilters = filters
            ? Array.isArray(filters)
                ? stringifyFilter(filters)
                : stringifyFilter([filters])
            : "";
        const localePrefixed = pageUrl
            ? `${locale ? `/${locale}${!pageUrl.match(/^\//) ? "/" : ""}` : ""}`
            : null;
        const gotoPage = pageUrl
            ? `${localePrefixed}${pageUrl}${
                  urlFilters.length ? `?filter=${urlFilters}&resetFilters=${Date.now()}` : ""
              }`
            : `${location.pathname}?filter=${urlFilters}&resetFilters=${Date.now()}${
                  anchor ? `#${anchor}` : ""
              }`;
        history.push(gotoPage);
    };

    useEffect(() => {
        setLocale(locale);
    }, [locale, setLocale]);

    return (
        <SovosProvider>
            <Provider
                user={userInformation}
                proxy={proxy}
                theme={theme}
                uploads={uploads}
                actions={{
                    pushModal,
                    pushDialog,
                    popModal,
                    uploadFiles,
                    submitTicket,
                    download,
                    submitAuditNote,
                    removeModal,
                    setDataFileStatus,
                    deleteFilePermanently,
                    deleteInvalidFilePermanently,
                    hideFile,
                    downloadFile,
                    reprocessRepository,
                    logout,
                    getFindingDetails,
                    downloadJSON,
                    setLocale: (locale) => {
                        history.push(`/${locale}${history.location.pathname.substring(3)}`);
                    },
                    search: genericSearchAction,
                    goto,
                    downloadableQueries,
                    reprocessFiles,
                }}
                i18n={i18n}
                locale={locale || localesByAppTheme[0].id}
                locales={localesByAppTheme}
                reload={reload}
                params={{
                    ...params,
                    fiscalYear: params.fiscalYear,
                    importId: params.importId,
                }}
                history={history}
                location={location}
                repositoryStatus={repositoryStatus}
                auditNote={auditNote}
                totalLinesCsv={getTotalExportLines || ""}
                customCardsCells={customCells}
                detailPanelComponents={detailPanels}
                pushNotification={pushNotification}
            >
                <PersistGate persistor={persistor}>
                    <div className={finalStyles["app"]}>
                        <>
                            <SovosPlatformProvider
                                suiteKey="tax"
                                productKey="saft"
                                environment={environmentProductSwitcherSetup}
                                onLoadingComplete={onLoadingComplete}
                            >
                                <SovosGlobalLayout NavigationProps={fullNavProps}>
                                    {children}
                                </SovosGlobalLayout>
                                <AboutThisDialog
                                    isOpen={isAboutThisDialogOpen}
                                    setIsAboutThisDialogOpen={
                                        setIsAboutThisDialogOpen || (() => {})
                                    }
                                    productName={"SAF-T"}
                                    localesByAppTheme={localesByAppTheme}
                                    i18n={i18n}
                                    learnMoreUrl={REACT_APP_LEARN_MORE_URL}
                                    sovosLinkURL={`${REACT_APP_SSO_BASE_URL}/my-profile/`}
                                    privacyPolicyUrl={REACT_APP_PRIVACY_POLICY_URL}
                                    termsAndConditionsUrl={REACT_APP_TERMS_AND_CONDITIONS_URL}
                                    version={version}
                                />
                            </SovosPlatformProvider>

                            <LanguageDialog
                                isOpen={isLanguageModalOpen}
                                setIsOpen={setIsLanguageModalOpen || (() => {})}
                                i18n={i18n}
                                setUserLocale={setUserLocale}
                                userLocale={userLocale}
                                localesByAppTheme={localesByAppTheme}
                                setLocale={setLocale}
                                history={history}
                            />
                        </>

                        <ModalController popModal={popModal} />
                        <DialogController i18n={i18n} />
                        <ToastContainer />
                        <SovosSnackbar
                            onClose={() => {
                                pushNotification({
                                    ...notification,
                                    open: false,
                                });
                            }}
                            message={notification && notification.message}
                            open={notification && notification.open}
                            variant={
                                notification && notification.type
                                    ? notification.type
                                    : "confirmation"
                            }
                            timeout={
                                notification && notification.timeout
                                    ? notification.timeout
                                    : "medium"
                            }
                        />
                    </div>
                </PersistGate>
            </Provider>
        </SovosProvider>
    );
};

export default connect(
    ({ user, fetch, menu, uploads, auditNote }) => ({
        user,
        fetch,
        menu,
        uploads,
        auditNote,
    }),
    {
        removeModal,
        detailPanels,
    },
)(withRouter(GlobalLayout));
